import { Component, Emit, Ref, Vue } from 'vue-property-decorator'
import ValuableConceptsModalComponent from '@/components/valuable-concepts-modal/valuable-concepts-modal.vue'
import ValuableConceptsModal from '@/components/valuable-concepts-modal/valuable-concepts-modal'
import BillingModalDeliveryNotesCrudComponent from '@/modules/billing-module/components/modal/delivery-notes/crud/billing-modal-delivery-notes-crud.vue'
import BillingModalDeliveryNotesCrud from '@/modules/billing-module/components/modal/delivery-notes/crud/billing-modal-delivery-notes-crud'
import BillingModalDeliveryNotesAddToComponent from '@/modules/billing-module/components/modal/delivery-notes/add-to/billing-modal-delivery-notes-add-to.vue'
import BillingModalDeliveryNotesAddTo from '@/modules/billing-module/components/modal/delivery-notes/add-to/billing-modal-delivery-notes-add-to'
import { AppliedBillableConceptResponse } from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.dto'
import { BillingStoreHelper } from '@/modules/billing-module/store/billing-store.helper'
import { MenuItemDef } from '@ag-grid-enterprise/all-modules'
import { StrictNullable } from '@/shared/types/app.types'
import { billingAppliedBillableConceptService } from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.service'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { ToastUtils } from '@/shared/util/toast-utils'
import { billingCommonDeliveryNoteService } from '@/api/services/billing/common-delivery-note/billing-common-delivery-note.service'
import { store } from '@/store/store'
import { HelperUtils } from '@/shared/util/helper-utils'
import { BillingOperation } from '@/api/operations/models/billing-operation'

@Component({
    components: {
        ValuableConceptsModalComponent,
        BillingModalDeliveryNotesAddToComponent,
        BillingModalDeliveryNotesCrudComponent
    }
})
export default class BillingGridActionsConcepts extends Vue {
    @Ref() readonly conceptsModal!: ValuableConceptsModal
    @Ref() readonly deliveryNotesModal!: BillingModalDeliveryNotesCrud
    @Ref() readonly deliveryNotesAddToModal!: BillingModalDeliveryNotesAddTo

    @Emit()
    refresh(): void {
        // emits an event
    }

    private static get _selectedRows(): AppliedBillableConceptResponse[] {
        return BillingStoreHelper.masterSelectedRows as AppliedBillableConceptResponse[]
    }
    private static get _selectedRow(): StrictNullable<AppliedBillableConceptResponse> {
        return BillingGridActionsConcepts._selectedRows.length === 1
            ? BillingGridActionsConcepts._selectedRows[0]
            : null
    }

    get canUpdateConcept(): boolean {
        return BillingGridActionsConcepts._selectedRows.length === 1 &&
            HelperUtils.hasOperationInCurrentLaboratory(BillingOperation.MANAGE_APPLIED_CONCEPTS)
    }
    get canDeleteConcept(): boolean {
        return !!BillingGridActionsConcepts._selectedRow &&
            !BillingGridActionsConcepts._selectedRow.deliveryNote &&
            BillingGridActionsConcepts._selectedRow.metadata.operationGuards.canBeDeleted &&
            HelperUtils.hasOperationInCurrentLaboratory(BillingOperation.MANAGE_APPLIED_CONCEPTS)
    }
    get canCreateDeliveryNote(): boolean {
        const hasAnySelectedRow =
            !!BillingGridActionsConcepts._selectedRows.length
        const sameAccountingUnit =
            BillingGridActionsConcepts._selectedRows.every(selectedRow =>
                selectedRow.rate?.accountingUnit.id === BillingGridActionsConcepts._selectedRows[0].rate?.accountingUnit.id)
        const sameIsForAutoInvoice =
            BillingGridActionsConcepts._selectedRows.every(selectedRow =>
                selectedRow.isForAutoInvoice === BillingGridActionsConcepts._selectedRows[0].isForAutoInvoice)
        const sameCustomerOrPatient =
            BillingGridActionsConcepts._selectedRows.every(selectedRow =>
                (!!selectedRow.customer && selectedRow.customer.id === BillingGridActionsConcepts._selectedRows[0].customer?.id) ||
                (!selectedRow.customer && selectedRow.caseStudy.patient.id === BillingGridActionsConcepts._selectedRows[0].caseStudy.patient.id))
        const hasDeliveryNote = BillingGridActionsConcepts._selectedRows.every(selectedRow => !!selectedRow.deliveryNote)

        /// breakdowns
        const customerHasBreakdownByCaseType =
            BillingGridActionsConcepts._selectedRows.every(selectedRow =>
                !!selectedRow.customer &&
                selectedRow.customer.breakdownDeliveryNotesByCaseType &&
                selectedRow.caseStudy.caseType.id !== BillingGridActionsConcepts._selectedRows[0].caseStudy.caseType.id)
        const customerHasBreakdownByPatient =
            BillingGridActionsConcepts._selectedRows.every(selectedRow =>
                !!selectedRow.customer &&
                selectedRow.customer.breakdownDeliveryNotesByPatient &&
                selectedRow.caseStudy.patient.id !== BillingGridActionsConcepts._selectedRows[0].caseStudy.patient.id)
        const customerHasBreakdownByRegime =
            BillingGridActionsConcepts._selectedRows.every(selectedRow =>
                !!selectedRow.customer &&
                selectedRow.customer.breakdownDeliveryNotesByRegime &&
                selectedRow.caseStudy.patient.id !== BillingGridActionsConcepts._selectedRows[0].caseStudy.patient.id)

        return hasAnySelectedRow &&
            sameAccountingUnit &&
            sameIsForAutoInvoice &&
            sameCustomerOrPatient &&
            !customerHasBreakdownByCaseType &&
            !customerHasBreakdownByPatient &&
            !customerHasBreakdownByRegime &&
            !hasDeliveryNote &&
            HelperUtils.hasOperationInCurrentLaboratory(BillingOperation.MANAGE_DELIVERY_NOTES)
    }
    get canAddToDeliveryNote(): boolean {
        return this.canCreateDeliveryNote
    }

    /// SERVICE CALLS ///

    private async _deleteConcepts(request: EntityIdentifiersRequest): Promise<StrictNullable<EntityIdentifierWithErrorResponse[]>> {
        const ids = request.entityIdentifiers
        if (ids.length) {
            const response =
                await PromiseUtils.toCallSecure(
                    billingAppliedBillableConceptService.delete(
                        store.state.labId,
                        request))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    ToastUtils.showToastSuccess(this.$tc('accounting.concept.deleted', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    /// EVENT HANDLERS ///

    onUpdateConcept(): void {
        if (BillingGridActionsConcepts._selectedRow) {
            this.conceptsModal.show({
                isBillingModule: true,
                caseId: BillingGridActionsConcepts._selectedRow.caseStudy.id,
                accountingId: BillingGridActionsConcepts._selectedRow.unit?.id,
                entityConcept: {
                    entityId: BillingGridActionsConcepts._selectedRow.entityId,
                    entityType: BillingGridActionsConcepts._selectedRow.entityType.code
                }
            })
        }
    }

    async onDeleteConcept(): Promise<void> {
        if (BillingGridActionsConcepts._selectedRow) {
            const request: EntityIdentifiersRequest = {
                entityIdentifiers: [BillingGridActionsConcepts._selectedRow.id]
            }
            if (await this._deleteConcepts(request)) {
                this.refresh() // emits an event
            }
        }
    }

    onCreateDeliveryNote(): void {
        this.deliveryNotesModal.show({
            appliedBillableConcepts: BillingGridActionsConcepts._selectedRows
        })
    }

    async onAddToDeliveryNote(): Promise<void> {
        await this.deliveryNotesAddToModal.show({
            appliedBillableConcepts: BillingGridActionsConcepts._selectedRows
        })
    }
    async onAddedToDeliveryNote(deliveryNoteId: number): Promise<void> {
        this.deliveryNotesAddToModal.hide()
        this.refresh()

        const deliveryNote =
            await PromiseUtils.toCallSecure(
                billingCommonDeliveryNoteService.get(store.state.labId, deliveryNoteId))
        if (deliveryNote !== null) {
            this.deliveryNotesModal.show({ deliveryNote })
        }
    }

    ///

    getContextMenuItems(): MenuItemDef[] {
        const menuItems: MenuItemDef[] = []
        if (this.canDeleteConcept) {
            menuItems.push({
                name: this.$tc('accounting.action.deleteConcept'),
                action: this.onDeleteConcept
            })
        }
        if (this.canUpdateConcept) {
            menuItems.push({
                name: this.$tc('accounting.action.updateConcept'),
                action: this.onUpdateConcept
            })
        }
        if (this.canCreateDeliveryNote) {
            menuItems.push({
                name: this.$tc('accounting.action.generateDeliveryNote'),
                action: this.onCreateDeliveryNote
            })
        }
        if (this.canAddToDeliveryNote) {
            menuItems.push({
                name: this.$tc('accounting.action.addToDeliveryNote'),
                action: this.onAddToDeliveryNote
            })
        }
        return menuItems
    }
}
